import React, { useMemo, useState } from "react";
import { StatusPill, EmptyState } from "../../../../components/ui/PageBits";
import { fmtTime } from "../../../Booking/bookingService";
import { shopNameOf, resourceNameOf, statusTone } from "./ownerBits";
import BookingEditModal from "../../../Booking/components/shop/BookingEditModal";

/**
 * Platform-wide bookings across all shops, with shop + status + text
 * filters. For single-shop work, use impersonation from the Shops tab.
 */
export default function BookingsTab({ bookings, shops, resources, onCancel, onEdit, onStatus }) {
    const [shopId, setShopId] = useState("all");
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [editing, setEditing] = useState(null);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return [...bookings]
            .filter((b) => (shopId === "all" ? true : b.ownerId === shopId))
            .filter((b) => (status === "all" ? true : b.status === status))
            .filter((b) => {
                if (!needle) return true;
                return [b.id, b.customer, b.email, b.type].join(" ").toLowerCase().includes(needle);
            })
            .sort((a, b) => `${b.date} ${b.start}`.localeCompare(`${a.date} ${a.start}`));
    }, [bookings, shopId, status, q]);

    return (
        <div className="space-y-4">
            <div className="hk-card">
                <div className="flex flex-col md:flex-row gap-2">
                    <input
                        className="hk-input md:flex-1"
                        placeholder="Search ref, customer, email…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <div className="flex gap-2">
                        <select className="hk-select" value={shopId} onChange={(e) => setShopId(e.target.value)}>
                            <option value="all">All shops</option>
                            {shops.map((s) => (
                                <option key={s.id} value={s.id}>{s.business}</option>
                            ))}
                        </select>
                        <select className="hk-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="all">All statuses</option>
                            {["pending", "confirmed", "completed", "cancelled", "noshow"].map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="hk-table-wrap">
                {filtered.length === 0 ? (
                    <EmptyState icon="📅" title="No bookings match" hint="Try different filters." />
                ) : (
                    <table className="hk-table">
                        <thead>
                            <tr>
                                <th>Ref</th>
                                <th>Customer</th>
                                <th>Shop · resource</th>
                                <th>When</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((b) => (
                                <tr key={b.id}>
                                    <td className="hk-mono text-xs">{b.id}</td>
                                    <td>
                                        <div className="font-bold">{b.customer}</div>
                                        <div className="text-xs text-[#64748b]">{b.type}</div>
                                    </td>
                                    <td>
                                        <div className="text-xs font-bold">{shopNameOf(shops, b.ownerId)}</div>
                                        <div className="text-xs text-[#64748b]">{resourceNameOf(resources, b.resourceId)}</div>
                                    </td>
                                    <td className="whitespace-nowrap text-xs hk-mono">{b.date} · {fmtTime(b.start)}</td>
                                    <td><StatusPill tone={statusTone(b.status)}>{b.status}</StatusPill></td>
                                    <td className="text-right whitespace-nowrap">
                                        <div className="flex gap-1 justify-end flex-wrap items-center">
                                            <select
                                                className="hk-select hk-mono"
                                                style={{ width: "auto", textTransform: "capitalize" }}
                                                value={b.status}
                                                title="Change booking status"
                                                onChange={(e) => onStatus(b.id, e.target.value)}
                                            >
                                                {["pending", "confirmed", "completed", "cancelled", "noshow"].map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                            <button
                                                className="hk-btn hk-btn-outline hk-btn-sm"
                                                onClick={() => setEditing(b)}
                                            >
                                                Edit
                                            </button>
                                            {b.status !== "cancelled" && (
                                                <button
                                                    className="hk-btn hk-btn-outline hk-btn-sm"
                                                    onClick={() => {
                                                        if (window.confirm(`Cancel booking ${b.id} (${b.customer})?`)) onCancel(b.id);
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <p className="text-xs text-[#64748b] px-1">{filtered.length} booking(s) shown.</p>

            {editing && (
                <BookingEditModal
                    booking={editing}
                    resources={resources}
                    bookings={bookings}
                    onClose={() => setEditing(null)}
                    onSave={(patch) => {
                        onEdit(editing.id, patch);
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
}
